/**
 * Real self-fetching leave payout-estimate + post-payout hook (#23311).
 * Extends the #23310 Leave module — `use_leave_requests.ts` reviews the
 * queue; this hook drives the cash-value preview and the real disbursement
 * mutation.
 *
 * ── The two paths, and why they live in the same hook
 *
 * `payroll.getLeavePayoutEstimate` (read-scoped preview) and
 * `payroll.postLeavePayout` (write-only real GL disbursement) are strongly
 * paired at the UX layer — the drawer never posts a payout without first
 * showing the caller the exact cash amount it will move. Keeping the read
 * + write in one hook mirrors the estimate/post pairing on the backend
 * (`getLeavePayoutEstimate` / `postLeavePayout` in payroll.ts).
 *
 * ── Failure classification (mirrors `use_leave_requests.ts`'s
 * `classifyActionFailure`)
 *
 * The mutation surface can fail multiple ways beyond the generic
 * `error`/`entitlement_required` bucket — the drawer needs to render each
 * as a helpful one-line inline error rather than a raw toast:
 *
 *   - `not_salaried` — BAD_REQUEST `'Leave payout requires a salaried
 *     employee with a monthly salary on file'` (both estimate and post can
 *     raise this; hourly employees are out of scope for the current
 *     daily-rate formula, `leave_payout_calculations.ts`).
 *   - `no_unused_balance` — BAD_REQUEST `'Employee has no unused leave
 *     balance to pay out'` OR `'Computed leave payout amount is zero'`.
 *     The latter is the same terminal condition after the estimate
 *     rounded to zero — a user can safely dismiss.
 *   - `stale_balance` — CONFLICT `'Leave balance changed since this
 *     payout was calculated -- please refresh and retry.'`. Emitted when
 *     the post-time CAS on `leave_balances.balance_days_scaled` sees the
 *     snapshot has changed under it (a concurrent accrual/approve/etc.).
 *     Refresh + retry is safe.
 *   - `bank_account_not_found` — NOT_FOUND on the bank-account leg (bad
 *     input; user should reselect).
 *   - `not_found` — NOT_FOUND on the employee row (usually a stale
 *     bookmark; refresh is safe).
 *   - `period_locked` — FORBIDDEN with the lock-date wording from
 *     `lock_date_service.ts` — the payout date is before the entity's
 *     hard/soft lock date and the caller cannot bypass. Distinguished
 *     from a plain OAuth `forbidden` so the drawer can suggest changing
 *     the date rather than a permissions retry.
 *   - `forbidden` — every other FORBIDDEN (missing OAuth scope, null
 *     audience, no entity access).
 *   - `entitlement_required` — the payroll add-on grant is missing.
 *   - `error` — everything else.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import {
  BALANCE_TRACKED_LEAVE_TYPES,
  derive_payout_estimate,
  type BalanceTrackedLeaveType,
  type LeavePayoutEstimate,
  type RealLeavePayoutEstimate,
} from './wagepro_store_leave_balances.js';

export { BALANCE_TRACKED_LEAVE_TYPES };
export type { BalanceTrackedLeaveType };

/** Input every payout call needs to identify the entity + employee. */
export interface LeavePayoutFetchInput {
  firm_id: string;
  entity_id: string;
  employee_id: string;
}

export type LeavePayoutLoadFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  | 'not_salaried'
  | 'no_unused_balance'
  | 'error';

/** All the ways `post_payout` can fail — a superset of the estimate load failures. */
export type LeavePayoutActionFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  | 'bank_account_not_found'
  | 'not_salaried'
  | 'no_unused_balance'
  | 'stale_balance'
  | 'period_locked'
  | 'error';

// ── Shared message prefixes / phrases the backend uses ─────────────────────
const NOT_SALARIED_PREFIX = 'Leave payout requires a salaried employee';
const NO_UNUSED_MESSAGE = 'Employee has no unused leave balance';
const ZERO_AMOUNT_MESSAGE = 'Computed leave payout amount is zero';
const STALE_BALANCE_PREFIX = 'Leave balance changed';
const BANK_ACCOUNT_MESSAGE = 'Bank account not found';
const EMPLOYEE_MESSAGE = 'Employee not found';
// Matches the two real backend messages emitted by
// `assertNotLocked` (`services/lock_date_service.ts`, prefix) and
// `assertNotInPeriodLock` (`services/period_lock.ts`, exact).
// Kept in sync with `post_run_statutory_error_classification.ts:116-117`.
const LOCK_DATE_MESSAGE_PREFIX = 'Your accounts are locked up to and including';
const PERIOD_LOCK_MESSAGE = 'Cannot post to a locked period';

interface TrpcErrorLike {
  data?: { code?: unknown };
}

function readTrpcCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('data' in err)) return;
  const code = (err as TrpcErrorLike).data?.code;
  return typeof code === 'string' ? code : undefined;
}

function readMessage(err: unknown): string {
  return err instanceof Error ? err.message : '';
}

/**
 * Classifies an estimate-read failure. Shared with `post_payout`'s
 * classifier — the estimate procedure raises the same NOT_FOUND /
 * not-salaried / no-unused-balance messages that the mutation does, so a
 * pre-flight estimate call surfaces most user-actionable errors before the
 * caller ever confirms the payout.
 */
export function classifyLeavePayoutEstimateFailure(
  err: unknown,
): LeavePayoutLoadFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  const code = readTrpcCode(err);
  const message = readMessage(err);
  if (code === 'NOT_FOUND') return 'not_found';
  if (code === 'BAD_REQUEST') {
    if (message.startsWith(NOT_SALARIED_PREFIX)) return 'not_salaried';
    if (
      message.startsWith(NO_UNUSED_MESSAGE) ||
      message.startsWith(ZERO_AMOUNT_MESSAGE)
    ) {
      return 'no_unused_balance';
    }
  }
  if (isForbiddenTrpcError(err)) return 'forbidden';
  return 'error';
}

/** Classifies a `postLeavePayout` mutation failure. */
export function classifyLeavePayoutActionFailure(
  err: unknown,
): LeavePayoutActionFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  const code = readTrpcCode(err);
  const message = readMessage(err);
  if (code === 'NOT_FOUND') {
    // The mutation raises identical `NOT_FOUND` codes for both the
    // employee and the bank-account IDOR guards; distinguish by message so
    // the drawer can nudge "reselect the account" vs. "refresh the page".
    if (message.startsWith(BANK_ACCOUNT_MESSAGE))
      return 'bank_account_not_found';
    if (message.startsWith(EMPLOYEE_MESSAGE)) return 'not_found';
    return 'not_found';
  }
  if (code === 'BAD_REQUEST') {
    if (message.startsWith(NOT_SALARIED_PREFIX)) return 'not_salaried';
    if (
      message.startsWith(NO_UNUSED_MESSAGE) ||
      message.startsWith(ZERO_AMOUNT_MESSAGE)
    ) {
      return 'no_unused_balance';
    }
  }
  if (code === 'CONFLICT' && message.startsWith(STALE_BALANCE_PREFIX)) {
    return 'stale_balance';
  }
  if (
    code === 'FORBIDDEN' &&
    (message.startsWith(LOCK_DATE_MESSAGE_PREFIX) ||
      message === PERIOD_LOCK_MESSAGE)
  ) {
    return 'period_locked';
  }
  if (isForbiddenTrpcError(err)) return 'forbidden';
  return 'error';
}

/** DI seams for the two calls. */
export interface LeavePayoutDeps {
  getEstimate: (
    input: LeavePayoutFetchInput & { as_of_date?: string },
  ) => Promise<RealLeavePayoutEstimate>;
  postPayout: (
    input: LeavePayoutFetchInput & {
      date: string;
      bank_account_id: string;
      leave_types?: readonly BalanceTrackedLeaveType[];
    },
  ) => Promise<unknown>;
}

function defaultLeavePayoutDeps(): LeavePayoutDeps {
  const client = getTrpcClient();
  return {
    getEstimate: (input) => client.payroll.getLeavePayoutEstimate.query(input),
    postPayout: (input) =>
      client.payroll.postLeavePayout.mutate({
        firm_id: input.firm_id,
        entity_id: input.entity_id,
        employee_id: input.employee_id,
        date: input.date,
        bank_account_id: input.bank_account_id,
        ...(input.leave_types !== undefined
          ? { leave_types: [...input.leave_types] }
          : {}),
      }),
  };
}

export type LeavePayoutEstimateResult =
  | { outcome: 'success'; estimate: LeavePayoutEstimate }
  | { outcome: 'failure'; failure: LeavePayoutLoadFailure };

/**
 * Pre-flight estimate. Never throws; every failure resolves to
 * `{outcome: 'failure', failure}` with one actionable `console.error`.
 * Resolves `deps` INSIDE the try so a synchronous `getTrpcClient()` throw
 * can't escape as an unhandled rejection (mirrors `approveLeaveRequest`).
 */
export async function fetchLeavePayoutEstimate(
  input: LeavePayoutFetchInput & { as_of_date?: string },
  deps?: LeavePayoutDeps,
): Promise<LeavePayoutEstimateResult> {
  try {
    const resolvedDeps = deps ?? defaultLeavePayoutDeps();
    const raw = await resolvedDeps.getEstimate(input);
    return { outcome: 'success', estimate: derive_payout_estimate(raw) };
  } catch (err) {
    console.error('[wagepro-web] leave payout: estimate failed:', err);
    return {
      outcome: 'failure',
      failure: classifyLeavePayoutEstimateFailure(err),
    };
  }
}

export type LeavePayoutActionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: LeavePayoutActionFailure };

/**
 * Real `postLeavePayout` call. Never throws; the mutation posts a real GL
 * cash-disbursement journal on the backend, so the classifier surfaces the
 * exact server-side rejection to the UI (a caller who sees `stale_balance`
 * can safely retry after refresh; a caller who sees `period_locked` must
 * change the date instead).
 */
export async function postLeavePayout(
  input: LeavePayoutFetchInput & {
    date: string;
    bank_account_id: string;
    leave_types?: readonly BalanceTrackedLeaveType[];
  },
  deps?: LeavePayoutDeps,
): Promise<LeavePayoutActionResult> {
  try {
    const resolvedDeps = deps ?? defaultLeavePayoutDeps();
    await resolvedDeps.postPayout(input);
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] leave payout: post failed:', err);
    return {
      outcome: 'failure',
      failure: classifyLeavePayoutActionFailure(err),
    };
  }
}

export interface UseLeavePayoutResult {
  /** Read helper — reachable for both read-only and write-capable grants. `null` when the entity context is unresolved (never in a real render tree once the AuthGate has resolved). */
  fetch_estimate:
    | ((options?: {
        as_of_date?: string;
      }) => Promise<LeavePayoutEstimateResult>)
    | null;
  /** Write helper — `undefined` for read-only OAuth grants (hides the affordance entirely; never a button that would 403). */
  post_payout:
    | ((input: {
        date: string;
        bank_account_id: string;
        leave_types?: readonly BalanceTrackedLeaveType[];
      }) => Promise<LeavePayoutActionResult>)
    | undefined;
  /** True when the entity context has resolved with an OAuth grant that can actually reach both procedures. */
  can_post: boolean;
}

/**
 * Wires the balances hook (via `retry` supplied by the caller) to the
 * payout flow. Kept a separate hook — `use_leave_balances.ts` is read-only
 * and consumable independently — so a screen that only shows balances
 * doesn't pull the mutation surface in.
 */
export function useLeavePayout(
  employee_id: string | null,
): UseLeavePayoutResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();

  const readyEntity =
    entityContext.status === 'ready' && employee_id !== null
      ? entityContext
      : null;
  const can_post = readyEntity !== null && isWriteCapableGrant(grant);

  const fetch_estimate = React.useMemo(() => {
    if (readyEntity === null || employee_id === null) return null;
    return (options?: {
      as_of_date?: string;
    }): Promise<LeavePayoutEstimateResult> =>
      fetchLeavePayoutEstimate({
        firm_id: readyEntity.firm_id,
        entity_id: readyEntity.entity_id,
        employee_id,
        ...(options?.as_of_date !== undefined
          ? { as_of_date: options.as_of_date }
          : {}),
      });
  }, [readyEntity, employee_id]);

  const post_payout = React.useMemo(() => {
    // `can_post` is `readyEntity !== null && isWriteCapableGrant(grant)`, so
    // `!can_post` short-circuiting narrows `readyEntity` to non-null via
    // TS's closure-const flow analysis (no extra `readyEntity === null`
    // check needed and eslint-typescript's `no-unnecessary-condition` would
    // flag one). `employee_id` doesn't get that same closure narrowing
    // through `can_post` — TS only tracks the direct `readyEntity` link —
    // so it needs its own explicit check.
    if (!can_post || employee_id === null) return undefined;
    const emp_id: string = employee_id;
    return (input: {
      date: string;
      bank_account_id: string;
      leave_types?: readonly BalanceTrackedLeaveType[];
    }): Promise<LeavePayoutActionResult> =>
      postLeavePayout({
        firm_id: readyEntity.firm_id,
        entity_id: readyEntity.entity_id,
        employee_id: emp_id,
        date: input.date,
        bank_account_id: input.bank_account_id,
        ...(input.leave_types !== undefined
          ? { leave_types: input.leave_types }
          : {}),
      });
  }, [can_post, readyEntity, employee_id]);

  return { fetch_estimate, post_payout, can_post };
}
